'use client';

import { createContext, startTransition, useContext, useOptimistic, useRef, type FC, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { WikiLink as Link } from './WikiLink';
import { cn } from '../../../utils/cn';

interface TabsAnchor {
  href: string;
  top: number;
  left: number;
}

// WHY: каждая категория — отдельная страница, ряд вкладок и экран с прокруткой при переходе монтируются заново — место нажатия
// хранится вне React, иначе не переживёт смену страницы
let tabsAnchor: TabsAnchor | null = null;

export const takeWikiTabsAnchor = (href: string): TabsAnchor | null => {
  const anchor = tabsAnchor;
  tabsAnchor = null;
  return anchor?.href === href ? anchor : null;
};

interface WikiCatalogNavValue {
  pendingHref: string | null;
  navigate: (href: string, tab: HTMLElement | null) => void;
}

const WikiCatalogNavContext = createContext<WikiCatalogNavValue | null>(null);

const useWikiCatalogNav = (): WikiCatalogNavValue => {
  const value = useContext(WikiCatalogNavContext);
  if (!value) throw new Error('WikiCatalogNav is missing above the catalog tabs');
  return value;
};

export const WikiCatalogNav: FC<{ children: ReactNode }> = ({ children }) => {
  const router = useRouter();
  const [pendingHref, setPendingHref] = useOptimistic<string | null>(null);

  const navigate = (href: string, tab: HTMLElement | null) => {
    const nav = tab?.closest('nav');
    if (nav && href !== window.location.pathname + window.location.search) {
      tabsAnchor = { href, top: nav.getBoundingClientRect().top, left: nav.scrollLeft };
    }
    // WHY: адрес ставится в своём переходе — выбранная вкладка и приглушённый список держатся ровно до показа новой страницы
    startTransition(() => {
      setPendingHref(href);
      router.push(href, { scroll: false });
    });
  };

  return <WikiCatalogNavContext value={{ pendingHref, navigate }}>{children}</WikiCatalogNavContext>;
};

export const WikiCategoryTab: FC<{ href: string; current: boolean; children: ReactNode }> = ({ href, current, children }) => {
  const { pendingHref, navigate } = useWikiCatalogNav();
  const ref = useRef<HTMLAnchorElement>(null);
  const selected = pendingHref === null ? current : pendingHref === href;
  return (
    <Link
      ref={ref}
      href={href}
      onNavigate={(e) => {
        e.preventDefault();
        navigate(href, ref.current);
      }}
      aria-current={current ? 'page' : undefined}
      className={cn(
        'flex h-10 items-center whitespace-nowrap rounded-[20px] px-[14px] text-[14px] leading-[18px] text-label-primary transition-colors',
        selected ? 'bg-sc-selected font-[590]' : 'font-[510] hover:bg-white/[0.04]',
      )}
    >
      {children}
    </Link>
  );
};

export const WikiCatalogResults: FC<{ className?: string; children: ReactNode }> = ({ className, children }) => {
  const { pendingHref } = useWikiCatalogNav();
  return (
    <div aria-busy={pendingHref !== null || undefined} className={cn('transition-opacity aria-busy:opacity-60', className)}>
      {children}
    </div>
  );
};
